import type { LifestylePrize, TicketProduct } from "@/app/redux/features/api/lifestyle.types";

/**
 * The five prize categories rendered on /prizes/lifestyle. These match the five
 * OurDailyPrizes macro instances in the Umbraco grid for the page, each of which
 * used to be its own AngularJS controller instance.
 *
 * "holidays" exists in BotB.Core.Utilities.PrizeCategories and in the mock
 * collections, but is not currently configured on this page.
 */
export const PRIZE_CATEGORIES = ["lifestylecars", "cash", "bikes", "other", "watches"] as const;

/** Carousel row omitted — the page renders a dedicated All prizes grid instead. */
export const ALL_PRIZES_COLLECTION_NAME = "ALL PRIZES";

export type PrizeCategory = (typeof PRIZE_CATEGORIES)[number];

/** Labels for the Categories dropdown and the All filters drawer. */
export const CATEGORY_LABELS: Record<PrizeCategory, string> = {
  lifestylecars: "Cars",
  cash: "Cash",
  bikes: "Bikes",
  other: "Other",
  watches: "Watches",
};

/** Tile imagery for the Categories tab of the filter modal. */
export const CATEGORY_IMAGES: Record<PrizeCategory, string> = {
  lifestylecars: "/assets/images/lifestyle/categories/cars.webp",
  cash: "/assets/images/lifestyle/categories/cash.webp",
  bikes: "/assets/images/lifestyle/categories/bikes.webp",
  other: "/assets/images/lifestyle/categories/other.webp",
  watches: "/assets/images/lifestyle/categories/watches.webp",
};

/**
 * Maps a prize's Umbraco `Subcategory` onto the category slug used by the API
 * and the URL. Ported from `$scope.subcategoryMap` in botb.dailycontroller.js.
 */
export const SUBCATEGORY_TO_CATEGORY: Record<string, string> = {
  Watch: "watches",
  Cash: "cash",
  Bike: "bikes",
  Holiday: "holidays",
  Other: "other",
  "Lifestyle Car": "lifestylecars",
};

export type SortKey = "recommended" | "price-high" | "price-low" | "a-z" | "z-a";

export const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: "recommended", label: "Recommended" },
  { key: "price-low", label: "Ticket price: low to high" },
  { key: "price-high", label: "Ticket price: high to low" },
  { key: "a-z", label: "A - Z" },
  { key: "z-a", label: "Z - A" },
];

export const DEFAULT_SORT: SortKey = "recommended";

/** Progressive reveal, replacing the Angular `repeatLimit` / `loadMore` pair. */
export const INITIAL_REVEAL_COUNT = 12;
export const REVEAL_INCREMENT = 12;

/** sessionStorage key for scroll restore (was the `botb-lifestyle-scroll` cookie). */
export const SCROLL_RESTORE_KEY = "botb-lifestyle-scroll";

export type FacetKey = "makes" | "styles";

/**
 * The make and style rows each category offers, again taken from the design's
 * five panels rather than from the data.
 *
 * Cash lists neither: every cash prize shares one make and one style, so both
 * screens would open with a single useless option. "Other" lists only styles,
 * under the name "Categories" — its prizes are a grab bag where the style tag
 * (Beach, Tech, Skiing…) is the only useful axis, and their makes are one-offs.
 */
export const FACET_ROWS: Record<PrizeCategory, Array<{ key: FacetKey; label: string }>> = {
  lifestylecars: [
    { key: "makes", label: "Make" },
    { key: "styles", label: "Style" },
  ],
  bikes: [
    { key: "makes", label: "Make" },
    { key: "styles", label: "Style" },
  ],
  watches: [
    { key: "makes", label: "Make" },
    { key: "styles", label: "Style" },
  ],
  other: [{ key: "styles", label: "Categories" }],
  cash: [],
};

/** Measurements the user picks from fixed bands rather than dragging. */
export type BucketKey = "ticket" | "topspeed" | "zero60" | "power";

/** Measurements that keep a two-handle slider, where any band would be arbitrary. */
export type RangeKey = "rrp" | "engine";

export type NumericKey = BucketKey | RangeKey;

/** A band within a bucket filter. `min` is inclusive, `max` exclusive. */
export interface FilterBucket {
  id: string;
  label: string;
  min?: number;
  max?: number;
}

interface NumericFilterBase {
  label: string;
  /** Categories this filter applies to; empty means all. */
  categories: PrizeCategory[];
  valueOf: (prize: LifestylePrize) => number;
}

export interface BucketFilter extends NumericFilterBase {
  kind: "bucket";
  key: BucketKey;
  buckets: FilterBucket[];
}

export interface RangeFilter extends NumericFilterBase {
  kind: "range";
  key: RangeKey;
  step: number;
  format: (value: number) => string;
}

/**
 * The numeric filters, in the order the drawer lists them.
 *
 * Most are bucketed: the design offers four bands with result counts, which
 * reads far better than a slider on a long tail (one £132k car would otherwise
 * stretch the RRP track so every other prize sits in the first inch). RRP keeps
 * its slider because its spread is the point, and displacement follows it.
 *
 * `categories` is taken row-for-row from the design's five category panels, not
 * inferred from the data: cars get the performance measurements, bikes swap
 * them for displacement, watches stop at ticket price, and cash offers nothing
 * but ticket price.
 */
export const NUMERIC_FILTERS: Array<BucketFilter | RangeFilter> = [
  {
    kind: "bucket",
    key: "ticket",
    label: "Ticket Price",
    categories: [],
    valueOf: prize => activeTicketPrice(prize),
    buckets: [
      { id: "under-1", label: "Under £1", max: 1 },
      { id: "1-2", label: "£1 - £1.99", min: 1, max: 2 },
      { id: "2-3", label: "£2 - £2.99", min: 2, max: 3 },
      { id: "over-3", label: "Over £3", min: 3 },
    ],
  },
  {
    kind: "range",
    key: "rrp",
    label: "RRP",
    categories: ["lifestylecars", "bikes"],
    valueOf: prize => prize.RetailPriceGbp ?? 0,
    step: 1000,
    format: value => formatGbp(value),
  },
  {
    kind: "bucket",
    key: "topspeed",
    label: "Top Speed",
    categories: ["lifestylecars"],
    valueOf: prize => prize.TopSpeed ?? 0,
    buckets: [
      { id: "under-120", label: "up to 120", max: 120 },
      { id: "120-139", label: "120 - 139", min: 120, max: 140 },
      { id: "140-159", label: "140 - 159", min: 140, max: 160 },
      { id: "over-160", label: "160 +", min: 160 },
    ],
  },
  {
    kind: "bucket",
    key: "zero60",
    label: "0 - 60 (s)",
    categories: ["lifestylecars"],
    valueOf: prize => parseFloat(prize.MphTime) || 0,
    buckets: [
      { id: "under-5", label: "Under 5s", max: 5 },
      { id: "5-7", label: "5 - 6.9s", min: 5, max: 7 },
      { id: "7-9", label: "7 - 8.9s", min: 7, max: 9 },
      { id: "over-9", label: "9s +", min: 9 },
    ],
  },
  {
    kind: "bucket",
    key: "power",
    label: "BHP",
    categories: ["lifestylecars"],
    valueOf: prize => prize.Power ?? 0,
    buckets: [
      { id: "under-150", label: "Under 150", max: 150 },
      { id: "150-249", label: "150 - 249", min: 150, max: 250 },
      { id: "250-399", label: "250 - 399", min: 250, max: 400 },
      { id: "over-400", label: "400 +", min: 400 },
    ],
  },
  {
    kind: "range",
    key: "engine",
    label: "Displacement",
    categories: ["bikes"],
    valueOf: prize => prize.EngineSizeCC ?? 0,
    step: 10,
    format: value => `${value.toLocaleString("en-GB")}cc`,
  },
];

export const BUCKET_FILTERS = NUMERIC_FILTERS.filter((filter): filter is BucketFilter => filter.kind === "bucket");

export const RANGE_FILTERS = NUMERIC_FILTERS.filter((filter): filter is RangeFilter => filter.kind === "range");

/**
 * Whether a prize falls in a band.
 *
 * A missing measurement reads as 0, which would otherwise land every cash prize
 * in "up to 120 mph", so anything without a real value matches no band and is
 * excluded once that filter is used at all.
 */
export function matchesBucket(value: number, bucket: FilterBucket): boolean {
  if (!Number.isFinite(value) || value <= 0) return false;
  if (bucket.min !== undefined && value < bucket.min) return false;
  if (bucket.max !== undefined && value >= bucket.max) return false;
  return true;
}

/** The ticket a customer actually buys: the sale ticket when it undercuts base. */
export function activeTicket(prize: LifestylePrize): TicketProduct | null {
  const { SaleTicket, BaseTicket } = prize;
  if (SaleTicket && BaseTicket) {
    return SaleTicket.Price < BaseTicket.Price ? SaleTicket : BaseTicket;
  }
  return SaleTicket ?? BaseTicket ?? null;
}

/** The price a customer actually pays: sale price when on offer, else base. */
export function activeTicketPrice(prize: LifestylePrize): number {
  return activeTicket(prize)?.Price ?? 0;
}

/**
 * The pre-sale price, which the card shows struck through beside the price a
 * customer actually pays. Null unless the sale ticket really undercuts base,
 * so a sale ticket priced at or above base does not draw a pointless "was".
 */
export function preSalePrice(prize: LifestylePrize): string | null {
  const { SaleTicket, BaseTicket } = prize;
  if (!SaleTicket || !BaseTicket) return null;
  return SaleTicket.Price < BaseTicket.Price ? BaseTicket.FormattedPrice : null;
}

/** Percentage off, for the hero's discount tag. 0 when not discounted. */
export function discountPercent(prize: LifestylePrize): number {
  const override = parseFloat(prize.OverrideDiscountValue);
  if (Number.isFinite(override) && override > 0) return Math.round(override);

  const base = prize.BaseTicket?.Price;
  const sale = prize.SaleTicket?.Price;
  if (!base || !sale || sale >= base) return 0;
  return Math.round(((base - sale) / base) * 100);
}

/** Bonus cash added on top of the prize, shown beside the model on the card. */
export function additionalCashGbp(prize: LifestylePrize): number {
  return prize.PrizeAdditionalCashGBP ?? prize.CompetitionAdditionalCashGBP ?? 0;
}

export function formatGbp(value: number): string {
  return `£${Math.round(value).toLocaleString("en-GB")}`;
}

/** Make and model, honouring the CMS switch used by some prize types. */
export function prizeTitle(prize: LifestylePrize): { primary: string; secondary: string } {
  return prize.SwitchMakeAndModel
    ? { primary: prize.Model ?? "", secondary: prize.Make ?? "" }
    : { primary: prize.Make ?? "", secondary: prize.Model ?? "" };
}

/** Revive ad zones for this page, carried over from `reviveAdEvents`. */
export const REVIVE_ID = "6ee315d200a003a5ac39b07d0dd34156";

export const REVIVE_ZONES = {
  card: {
    desktop: { zoneId: "24", source: "LS_card01_desk" },
    mobile: { zoneId: "25", source: "LS_card01_mob" },
  },
  wide: {
    desktop: { zoneId: "26", source: "LS_wide01_desk" },
    mobile: { zoneId: "27", source: "LS_wide01_mob" },
  },
} as const;

/**
 * Grid indexes the ads are injected after (prize then ad).
 * Card after the 2nd prize; wide after the 7th so that with the card in place
 * we land on 8 cells (two full desktop rows) before the banner — Angular used
 * insertBefore(prize[7]) which produced the same 8-cell count.
 */
export const REVIVE_CARD_INDEX = 1;
export const REVIVE_WIDE_INDEX = 6;

/** addToCartMethodId values the backend records for analytics. */
export const ADD_TO_CART_METHOD = {
  /** Quick-add straight from a prize tile. */
  quickAdd: 2,
  /** Through the ticket selector modal. */
  ticketSelector: 12,
} as const;
